import React, { ReactNode, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './Box.module.scss';
import Trigger from '../../widget/actuators/Trigger';

export interface BoxProps {
  children?: ReactNode;
  onClose?: () => void;
  width?: string;
  height?: string;
  maxHeight?: string;
  minWidth?: string;
  minHeight?: string;
  logo?: boolean;
  marginTop?: string;
  contentMargin?: string;
  contentPadding?: string;
  zIndex?: number;
  transparentOverlay?: boolean;
  /**
   * Fond plein au lieu du fond translucide des panneaux. Pour une fenêtre qui se
   * superpose à du contenu chargé : à 72 % d'opacité, ce qui est derrière transparaît
   * et gêne la lecture. Prend `--color-Panel-niv1-BG-solid`, la version pleine du
   * même fond, dans les deux thèmes.
   */
  solidBackground?: boolean;
}

export const Box = React.forwardRef<HTMLDivElement, BoxProps>(({
  children,
  onClose,
  width,
  height,
  maxHeight,
  minWidth,
  minHeight,
  logo = false,
  marginTop = '130px',
  contentMargin = '0px',
  contentPadding = '0px',
  zIndex = 9990,
  transparentOverlay = false,
  solidBackground = false,
}, ref) => {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [hasScroll, setHasScroll] = useState(false);

  // Remise à zéro du défilement uniquement à l'ouverture de la Box.
  // `children` change d'identité à chaque rendu du parent (même contenu) : le garder
  // en dépendance ramenait le contenu en haut à chaque re-rendu — typiquement à chaque
  // tick du polling de la liste, rendant toute Box longue impossible à parcourir.
  useEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    if (scrollableRef.current) {
      const el = scrollableRef.current;
      setHasScroll(el.scrollHeight > el.clientHeight);
    }
  }, [children, height, maxHeight]);

  const handleBoxClose = () => {
    if (onClose) onClose();
  };

  return (
    <>
      {/* transparentOverlay : Box imbriquée dans une autre Box — évite d'assombrir deux fois le fond */}
      <div className={styles.overlay} style={{ zIndex: zIndex - 1, ...(transparentOverlay && { backgroundColor: 'transparent' }) }} />
      <div
        ref={ref}
        className={styles.window_wrapper}
        style={{ width, height, maxHeight, minWidth, minHeight, zIndex }}
      >
        {onClose && (
          <div className={styles.window_close_button}>
            <Trigger
              iconName="cross"
              iconWidth={20}
              iconHeight={20}
              onClick={handleBoxClose}
            />
          </div>
        )}

        <div
          className={classNames(onClose ? styles.window_root : styles.root)}
          style={{ width: '100%', ...(solidBackground && { background: 'var(--color-Panel-niv1-BG-solid)' }) }}
        >
          {logo && (
            <div className={styles.window_logo}>
              <img src="/images/logo.png" alt="Logo" className={styles.logo} />
            </div>
          )}

          <div
            ref={scrollableRef}
            className={classNames(
              styles.window_scrollable,
              hasScroll && styles.with_scroll
            )}
            style={{ marginTop }}
          >
            <div
              className={styles.window_content}
              style={{
                margin: contentMargin,
                padding: contentPadding,
              }}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

export default Box;
